import { useTranslation } from 'react-i18next';
import { FC, useState } from 'react';
import { homeTranslations, productCategoryTranslations } from '../../../translations';
import { Button, DropdownMenu, DropdownMenuItem, FilterDrawer, FilterType } from '../../../stories/molecules';
import { SortDirection } from '../../../global-state/types';
import { ProductItem } from '../ProductCategoryPage';
import { ExpandButtonContainer } from '../ProductCategory.styles';
import { Container, Icon, Icons, Typography } from '../../../stories/atoms';

export type ProductCategoryMobileHeaderProps = {
  filteredProductItems: ProductItem[];
  indexDropdownMenuItems: DropdownMenuItem[];
  subIndexDropdownMenuItems: DropdownMenuItem[];
  filteredIndexDescriptions: string[];
  filteredSubIndexDescriptions: string[];
  isAnyExpanded: boolean;
  isNoData: boolean;
  updateFilteredItems: (descriptions: string[], itemType: 'index' | 'subindex') => void;
  sortValue: string | null;
  handleSortValue: (value: SortDirection) => void;
  clearAllFilters: () => void;
  onSettingsMenuSelect: (selectedItem: string[]) => void;
};

export const ProductCategoryMobileHeader: FC<ProductCategoryMobileHeaderProps> = ({
  filteredProductItems,
  indexDropdownMenuItems,
  subIndexDropdownMenuItems,
  filteredIndexDescriptions,
  filteredSubIndexDescriptions,
  isAnyExpanded,
  isNoData,
  updateFilteredItems,
  sortValue,
  handleSortValue,
  clearAllFilters,
  onSettingsMenuSelect,
}) => {
  const { t } = useTranslation();
  const { common: commonTranslations } = homeTranslations;
  const [isFilterDrawerOpen, setIsFilterDrawerOpen] = useState(false);

  const getTooltipText = () => {
    return (
      <Container padding={[1]}>
        <Typography color="white">
          {t(
            productCategoryTranslations.noData.subtitle.key,
            productCategoryTranslations.noData.subtitle.defaultValue,
          )}
        </Typography>
      </Container>
    );
  };

  const menuItems = [
    {
      key: 'toggleAll',
      disabled: isNoData,
      hasTooltip: isNoData,
      tooltipText: getTooltipText(),
      item: (
        <Container wrap="nowrap" direction="horizontal" style={{ gap: '8px' }}>
          <Icon icon={isAnyExpanded ? Icons.CHECKBOX_INDETERMINATE : Icons.PLUS} size="small" />
          <Typography>
            {isAnyExpanded
              ? t(commonTranslations.collapseAll.key, commonTranslations.collapseAll.defaultValue)
              : t(commonTranslations.expandAll.key, commonTranslations.expandAll.defaultValue)}
          </Typography>
        </Container>
      ),
    },
    {
      key: 'showGarmentGroups',
      disabled: isNoData,
      hasTooltip: isNoData,
      tooltipText: getTooltipText(),
      item: (
        <Container wrap="nowrap" direction="horizontal" style={{ gap: '8px' }}>
          <Icon icon={Icons.ALLWIDGETS} size="small" />
          <Typography>
            {t(
              productCategoryTranslations.garmentGroup.viewAllbutton.key,
              productCategoryTranslations.garmentGroup.viewAllbutton.defaultValue,
            )}
          </Typography>
        </Container>
      ),
    },
  ];

  const handleFilteredItems = (items: string[], type: 'index' | 'subindex') => {
    updateFilteredItems(items, type);
  };

  return (
    <>
      <FilterDrawer
        isOpen={isFilterDrawerOpen}
        onDrawerClose={() => setIsFilterDrawerOpen(false)}
        onClearAllFilters={clearAllFilters}
        totalResultEntries={filteredProductItems.length}
        totalResultEntriesText={t(
          productCategoryTranslations.filter.entriesText.key,
          productCategoryTranslations.filter.entriesText.defaultValue,
        )}
        filtersSpecs={[
          {
            key: 'sortBy',
            type: FilterType.RadioList,
            title: t(
              productCategoryTranslations.filter.drawerSort.key,
              productCategoryTranslations.filter.drawerSort.defaultValue,
            ),
            options: [
              {
                key: SortDirection.Ascending.toString(),
                title: t(
                  productCategoryTranslations.filter.sortAscending.key,
                  productCategoryTranslations.filter.sortAscending.defaultValue,
                ),
              },
              {
                key: SortDirection.Descending.toString(),
                title: t(
                  productCategoryTranslations.filter.sortDescending.key,
                  productCategoryTranslations.filter.sortDescending.defaultValue,
                ),
              },
            ],
            selectedOptionKey: sortValue ? sortValue.toString() : null,
            onSelect: (key) => handleSortValue(key as unknown as SortDirection),
          },
          {
            key: 'index',
            type: FilterType.MultiSelectList,
            title: t(
              productCategoryTranslations.filter.index.key,
              productCategoryTranslations.filter.index.defaultValue,
            ),
            options: indexDropdownMenuItems.map((item) => ({
              key: item.key,
              title: item.key,
              disabled: item.disabled,
            })),
            selectedOptionsKeys: filteredIndexDescriptions,
            onSelect: (selectedItemKeys) => handleFilteredItems(selectedItemKeys, 'index'),
          },
          {
            key: 'subIndex',
            type: FilterType.MultiSelectList,
            title: t(
              productCategoryTranslations.filter.subIndex.key,
              productCategoryTranslations.filter.subIndex.defaultValue,
            ),
            options: subIndexDropdownMenuItems.map((item) => ({
              key: item.key,
              title: item.key,
              disabled: item.disabled,
            })),
            selectedOptionsKeys: filteredSubIndexDescriptions,
            onSelect: (selectedItemKeys) => handleFilteredItems(selectedItemKeys, 'subindex'),
          },
        ]}
      />

      <Button
        data-testid="all-filters-button"
        label={t(
          productCategoryTranslations.filter.allFilters.key,
          productCategoryTranslations.filter.allFilters.defaultValue,
        )}
        buttonContentProps={{
          labelPosition: 'left',
          iconOptions: {
            icon: Icons.FILTER,
            size: 'small',
          },
          textOptions: {
            whiteSpace: 'nowrap',
          },
        }}
        aria-haspopup="true"
        aria-expanded={isFilterDrawerOpen ? 'true' : undefined}
        onClick={() => setIsFilterDrawerOpen(true)}
        variant="text"
      />

      <ExpandButtonContainer>
        <DropdownMenu
          data-testid="settingsIcons"
          dropdownLabel={''}
          buttonContentProps={{
            iconOptions: { customIcon: <Icon icon={Icons.SETTINGS} /> },
          }}
          menuItems={menuItems}
          minWidth={'0px'}
          isCompact
          initialSelected={[]}
          onSelect={(selectedItem) => onSettingsMenuSelect(selectedItem)}
          isOnlyIcon
          allowTooltip
        />
      </ExpandButtonContainer>
    </>
  );
};
